/**
 * PlatformUrlsSettings — configure each platform's public URLs.
 *
 * These URLs are used by the payment-link builder so buyers get redirected
 * back to the ORIGINATING platform site (cloudgm.business, fitness606.com,...)
 * after paying through Tamara / Stripe / Tabby / Ziina — instead of landing
 * on the admin backend.
 *
 * If a platform hasn't been deployed yet, its URL fields can stay empty and
 * the admin-hosted fallback receipt page (`/pay/receipt/{id}`) will be used.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Loader2, RefreshCw, Info, Globe, Copy, ExternalLink,
} from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getAuthHeaders } from '../../utils/authHeaders';
import { FINANCE_PLATFORM_META } from '../../constants/financePlatforms';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  'cloudgm_customers', 'cloudgm_merchants',
  'fitness', 'cgm_coin', 'community', 'mediadocs',
];

export default function PlatformUrlsSettings() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/platform-urls`, {
        headers: getAuthHeaders(),
      });
      setRows(r.data.data || []);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const d = {};
    rows.forEach(r => {
      d[r.platform] = {
        homepage_url: r.homepage_url || '',
        success_url: r.success_url || '',
        cancel_url: r.cancel_url || '',
        logo_url: r.logo_url || '',
      };
    });
    setDrafts(d);
  }, [rows]);

  const setDraft = (platform, patch) =>
    setDrafts(state => ({
      ...state,
      [platform]: { ...(state[platform] || {}), ...patch },
    }));

  const save = async (platform) => {
    const d = drafts[platform] || {};
    setSavingKey(platform);
    try {
      await axios.put(
        `${API_URL}/api/admin/platform-urls/${platform}`,
        { platform, ...d },
        { headers: getAuthHeaders() },
      );
      toast.success(t('✓ تم الحفظ', '✓ Saved'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingKey(null);
    }
  };

  const copyText = (v) => {
    navigator.clipboard.writeText(v).then(() => toast.success(t('تم النسخ', 'Copied')));
  };

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/settings" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: '/dashboard/finance/settings' },
            { label_ar: 'روابط المنصات', label_en: 'Platform URLs', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className={isAr ? 'text-right' : 'text-left'}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {t('روابط المنصات', 'Platform URLs')}
            </h1>
            <p className="text-white/50 text-sm max-w-2xl">
              {t('اضبط الرابط الرئيسي وصفحة استلام الدفع لكل منصة. تُستخدم هذه الروابط لإعادة توجيه العميل بعد الدفع (Tamara / Stripe / Tabby / Ziina).',
                 'Configure each platform\'s home URL and payment-success page. These are used to redirect the customer back to the platform after checkout.')}
            </p>
          </div>
          <Button onClick={load} variant="outline"
                  className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                  data-testid="purl-refresh">
            <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/20 text-blue-200/80 text-xs flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                {t('إذا تركت الحقول فارغة، سيُستخدم رابط الاستلام الاحتياطي المستضاف من admin (`/pay/receipt/{id}`) بلوغو المنصة.',
                   'If you leave the fields empty, the admin-hosted fallback receipt page (`/pay/receipt/{id}`) will be used with the platform\'s branding.')}
              </p>
            </div>

            {PLATFORMS.map((platform) => {
              const meta = FINANCE_PLATFORM_META[platform] || {};
              const d = drafts[platform] || {};
              const row = rows.find(x => x.platform === platform) || {};
              const isDirty =
                d.homepage_url !== (row.homepage_url || '')
                || d.success_url  !== (row.success_url  || '')
                || d.cancel_url   !== (row.cancel_url   || '')
                || d.logo_url     !== (row.logo_url     || '');

              return (
                <div key={platform}
                     className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
                     data-testid={`purl-card-${platform}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: meta.color }} />
                    <h3 className="text-white font-bold">{isAr ? meta.ar : meta.en}</h3>
                    <span className="text-white/40 text-xs font-mono ms-auto">{platform}</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={t('الرابط الرئيسي', 'Homepage URL')}
                           hint={t('مثلاً: https://cloudgm.business', 'e.g. https://cloudgm.business')}>
                      <Input
                        value={d.homepage_url}
                        onChange={(e) => setDraft(platform, { homepage_url: e.target.value })}
                        placeholder="https://cloudgm.business"
                        dir="ltr"
                        className="bg-[#050505] border-white/10 text-white"
                        data-testid={`purl-homepage-${platform}`}
                      />
                    </Field>
                    <Field label={t('صفحة نجاح الدفع', 'Payment success URL')}
                           hint={t('اتركه فارغ ليستخدم {home}/payment-success', 'Leave empty to auto-use {home}/payment-success')}>
                      <Input
                        value={d.success_url}
                        onChange={(e) => setDraft(platform, { success_url: e.target.value })}
                        placeholder="https://cloudgm.business/payment-success"
                        dir="ltr"
                        className="bg-[#050505] border-white/10 text-white"
                        data-testid={`purl-success-${platform}`}
                      />
                    </Field>
                    <Field label={t('صفحة إلغاء الدفع', 'Payment cancel URL')}
                           hint={t('اتركه فارغ ليستخدم {home}/payment-cancelled', 'Leave empty to auto-use {home}/payment-cancelled')}>
                      <Input
                        value={d.cancel_url}
                        onChange={(e) => setDraft(platform, { cancel_url: e.target.value })}
                        placeholder="https://cloudgm.business/payment-cancelled"
                        dir="ltr"
                        className="bg-[#050505] border-white/10 text-white"
                        data-testid={`purl-cancel-${platform}`}
                      />
                    </Field>
                    <Field label={t('لوغو المنصة (رابط صورة)', 'Platform logo URL')}
                           hint={t('يظهر في صفحة الاستلام الاحتياطية', 'Shown on the fallback receipt page')}>
                      <Input
                        value={d.logo_url}
                        onChange={(e) => setDraft(platform, { logo_url: e.target.value })}
                        placeholder="https://..."
                        dir="ltr"
                        className="bg-[#050505] border-white/10 text-white"
                        data-testid={`purl-logo-${platform}`}
                      />
                    </Field>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px] text-xs text-white/40 flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5" />
                      {d.homepage_url ? (
                        <a href={d.homepage_url} target="_blank" rel="noreferrer"
                           className="hover:text-white/80 truncate font-mono flex items-center gap-1" dir="ltr">
                          {d.homepage_url}
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </a>
                      ) : (
                        <span>{t('لم يُضبَط - سيُستخدم Fallback', 'Not set — fallback will be used')}</span>
                      )}
                    </div>
                    <Button
                      onClick={() => save(platform)}
                      disabled={!isDirty || savingKey === platform}
                      className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold disabled:opacity-50"
                      data-testid={`purl-save-${platform}`}
                    >
                      {savingKey === platform
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <><Save className="w-4 h-4 me-1" />{t('حفظ', 'Save')}</>}
                    </Button>
                  </div>

                  {/* Handy: API endpoint hint the platform's Agent can copy */}
                  <div className="mt-3 p-3 rounded-lg bg-black/40 border border-white/5">
                    <div className="text-[11px] text-white/40 mb-1.5">
                      {t('انسخ هذا الـ endpoint وأعطه لـ agent المنصة عشان يبني صفحة الاستلام:',
                         'Copy this endpoint and give it to the platform Agent so they can build the success page:')}
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs text-emerald-300 font-mono break-all" dir="ltr">
                        GET {API_URL}/api/public/receipt/{'{invoice_id}'}
                      </code>
                      <button
                        onClick={() => copyText(`${API_URL}/api/public/receipt/{invoice_id}`)}
                        className="p-1.5 rounded hover:bg-white/5 text-white/60"
                        data-testid={`purl-copy-api-${platform}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}


function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-white/80 text-xs font-medium mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-white/40 mt-1">{hint}</div>}
    </div>
  );
}
